import {
  BackButton,
  Box,
  ButtonStyled,
  FileUploaderStyled,
  FormElementWrapper,
  LabelStyled,
  LazyImage,
  NotAuthorizedForResource,
  SeparatorBorder,
  SpanStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../../../components/styled/Elements";
import { Col, FormGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FormDataContainer, ProfileWrapper } from "./styled";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../../../components/admin/Breadcrumbs/Breadcrumbs";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { ValidatedInputWrapper } from "../../../../../components/common/inputFields/ValidatedInput";
import TypeSelection from "./TypeSelection";
import { addArchiveRadio } from "../../../../../helpers/dummyConstants";
import {
  getIsTemporary,
  hasPlanFeature,
  reqFormatDate,
} from "../../../../../utils";
import AccessType from "./AccessType";
import Statuses from "./Statuses";
import DatePickerDefault from "../../../../../components/common/inputFields/DatePicker";
import ArchiveDateField from "./ArchiveDateField";
import { DateIcon, MenuTriggerCross, PlaceholderImg } from "../../../../../components/styled/AllImages";
import AccessSettings from "../../components/AccessSettings";
import { useEffect, useState } from "react";
import CabinetsApi from "../../../../../services/api/cabinetsApi";
import { toast } from "react-toastify";
import RequestsApi from "../../../../../services/api/requestsApi";
import { UserUpload } from "../../../../../components/styled/styled";

const EditArchive = () => {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id, fileId, archiveId } = useParams();

  const isTemporary = getIsTemporary();
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [accessThroughRequest, setAccessThroughRequest] = useState([]);

  const [userSelections, setUserSelections] = useState({
    branch_ids: [],
    department_ids: [],
    user_ids: [],
  });

  const validationSchema = Yup.object().shape(
    {
      my_department_id: Yup.number()
        .nullable()
        .when(["other_department_id", "setting_id"], {
          is: (otherId, settingId) => !otherId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      other_department_id: Yup.number()
        .nullable()
        .when(["my_department_id", "setting_id"], {
          is: (myId, settingId) => !myId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      setting_id: Yup.number()
        .nullable()
        .when(["my_department_id", "other_department_id"], {
          is: (myId, otherId) => !myId && !otherId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      my_dept_ids: Yup.array()
        .ensure()
        .when("my_department_id", {
          is: (val) => val === 4 || val === 5,
          then: (schema) =>
            schema
              .min(1, t("Please_select_user"))
              .required(t("Please_select_user")),
          otherwise: (schema) => schema.notRequired(),
        }),

      other_dept_ids: Yup.array()
        .ensure()
        .when("other_department_id", {
          is: (val) => [6, 7, 8, 9].includes(val),
          then: (schema) =>
            schema.min(1, t("Required")).required(t("Required")),
          otherwise: (schema) => schema.notRequired(),
        }),
    },
    [
      // Cyclic dependencies array
      ["my_department_id", "other_department_id"],
      ["my_department_id", "setting_id"],
      ["other_department_id", "setting_id"],
    ],
  );

  const getSingleArchive = async () => {
    try {
      setAccessDenied(false);
      setLoading(true);
      const response = await CabinetsApi.getSingleArchive(
        id,
        fileId,
        archiveId,
      );

      if (response.code === 403) {
        setAccessDenied(true);
      }

      if (response?.data) {
        const archiveData = response.data;
        const allReferrals = archiveData?.referrals || [];

        let my_department_id = null;
        let my_dept_ids = [];
        let other_department_id = null;
        let other_dept_ids = [];
        let common_settings_id = null;
        let from_branches = [];
        let from_departments = [];
        let checkedUsers = [];

        archiveData?.file_archive_details?.forEach((detail) => {
          if (detail.group_type === "my_department") {
            my_department_id = detail.common_settings_id;
            my_dept_ids = detail.selected_users?.map((x) => Number(x)) || [];
          } else if (detail.group_type === "other_department") {
            other_department_id = detail.common_settings_id;
            other_dept_ids =
              detail.selected_users?.map((x) => Number(x)) || detail.selected_departments?.map((x) => Number(x)) || [];
            from_branches = detail.from_branches?.map((x) => Number(x)) || [];
            from_departments = detail.from_departments?.map((x) => Number(x)) || [];

            // Fixed the reference error here: Use 'allReferrals' instead of 'response.data.cabinet.referrals'
            checkedUsers =
              detail?.selected_users_data?.map((item) => {
                const uKey = `${detail.group_type}_${item.id}`;
                const matchingReferral = allReferrals.find(
                  (ref) =>
                    ref.referred_to_id === item.id &&
                    ref.type === detail.group_type,
                );

                return {
                  ...item,
                  subLabel:
                    language === "en"
                      ? `${item?.user_detail?.department_selective?.name_en} (${item?.user_detail?.office_location_selective?.name_en})`
                      : `${item?.user_detail?.department_selective?.name_ar} (${item?.user_detail?.office_location_selective?.name_ar})`,
                  referralUniqueKey: uKey,
                  referralNote: matchingReferral ? matchingReferral.note : "",
                  shareReferrals: !!matchingReferral,
                };
              }) || [];
          } else if (
            detail.group_type === "myself" ||
            detail.group_type === "base_cabinet"
          ) {
            common_settings_id = detail.common_settings_id;
          }

          setInitialValues({
            action: "update",
            archive_id: archiveData?.id,
            title: archiveData?.title ?? "",
            description: archiveData?.description ?? "",
            archive_type_id:
              archiveData?.archive_type_id ?? addArchiveRadio[0]?.id,
            attachment: decodeURIComponent(archiveData?.attachment)
              .split("/")
              .pop(),
            access_type: archiveData?.access_type,
            other_type: archiveData?.other_type,
            archive_status_id: archiveData?.archive_status_id,
            date: archiveData?.date,
            no_of_pages: archiveData?.number_of_pages,
            number: archiveData?.number,
            from: archiveData?.from,
            to: archiveData?.to,
            finalizedFileUrl: archiveData?.attachment,
            setting_id: archiveData.common_settings_id || common_settings_id,
            my_department_id: my_department_id,
            my_dept_ids: my_dept_ids,
            other_department_id: other_department_id,
            other_dept_ids: other_dept_ids,
            referrals: allReferrals?.map((ref) => ({
              unique_id: `${ref.type}_${ref.referred_to_id}`,
              user_id: ref.referred_to_id,
              is_share: 1,
              note: ref.note,
              group: ref.type,
            })),
            checkedUsers: checkedUsers,
            from_branches: from_branches,
            from_departments: from_departments,
            checkedHqBranches: {
              branchIds: from_branches,
              depIds: from_departments,
            },
          });
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRequest = async (id) => {
    try {
      const { code, message } = await RequestsApi.removeRequest({ id });
      if (code === 200) {
        setAccessThroughRequest(
          accessThroughRequest.filter((item) => item.id != id),
        );
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {}
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const formData = new FormData();

      formData.append("action", values.action ?? "update");
      formData.append("archive_id", values.archive_id ?? archiveId);
      formData.append("title", values.title);
      formData.append("common_settings_id", values.setting_id ? Number(values.setting_id) : null);
      formData.append("description", values.description);
      formData.append("archive_type_id", values.archive_type_id);
      formData.append("archive_status_id", values.archive_status_id);
      formData.append("date", reqFormatDate(values.date));
      formData.append("number", values.number);
      formData.append("from", values.from);
      formData.append("to", values.to);
      formData.append("file_url", values.finalizedFileUrl);
      formData.append("multipart_flag", !!values.finalizedFileUrl);
      formData.append("no_of_pages", values.no_of_pages);
      formData.append("attachment", values.attachment);
      formData.append("duration", values.duration);
      formData.append("other_type", values.other_type);
      formData.append("is_temporary", isTemporary);
      formData.append("my_department_id", values.my_department_id ? Number(values.my_department_id) : null);
      formData.append(
        "other_department_id", values.other_department_id ?
        Number(values.other_department_id) : null,
      );
      appendArray(formData, "from_branches", values.from_branches ?? []);
      appendArray(formData, "from_departments", values.from_departments ?? []);
      appendArray(formData, "my_dept_ids", values.my_dept_ids ?? []);
      appendArray(formData, "other_dept_ids", values.other_dept_ids ?? []);
      formData.append("referrals", JSON.stringify(values.referrals ?? []));
      formData.append("access_type", values.access_type);

      const { code, message } = await CabinetsApi.createArchive(
        id,
        fileId,
        formData,
      );
      if (code === 200) {
        toast.success(t("archive_updated"));
        navigate(`/cabinets/${id}/files/${fileId}/archives`);
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setSubmitting(false);
    }
  };

  const appendArray = (formData, key, array) => {
    if (Array.isArray(array)) {
      array.forEach((item) => formData.append(`${key}[]`, Number(item)));
    }
  };

  // Load archive data on mount
  useEffect(() => {
    getSingleArchive();
  }, [id, fileId, archiveId]);

  if (loading) {
    return (
      <ProfileWrapper>
        <ThemeLoader />
      </ProfileWrapper>
    );
  }

  if (accessDenied) {
    return <NotAuthorizedForResource />;
  }

  if (!initialValues) {
    return (
      <ProfileWrapper>
        <ThemeLoader />
      </ProfileWrapper>
    );
  }

  return (
    <ProfileWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("add_archive")}
            onClick={() => navigate(`/cabinets/${id}/files/${fileId}/archives`)}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <Box className="content-container">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, errors }) => {
            console.log('errors: ', errors);
            return (
              <Form encType="multipart/form-data">
                <Row className="mt-3">
                  <Col md={12}>
                    <FormDataContainer className="dataContainer">
                      <ValidatedInputWrapper>
                        <FormGroup className="formEleContainer">
                          <LabelStyled
                            className="border-0"
                            size="15px"
                            color="var(--text)"
                          >
                            {t("title")}
                            <span className="staric">*</span>
                          </LabelStyled>

                          <Box className="w-100">
                            {/* Input Field */}
                            <Field
                              type="text"
                              name="title"
                              className="form-control"
                              placeholder={t("title")}
                            />
                            {/* Error Message */}
                            <ErrorMessage
                              name="title"
                              component="div"
                              className="text-danger text-sm mb-0 mt-1"
                            />
                          </Box>
                        </FormGroup>
                      </ValidatedInputWrapper>

                      <TypeSelection />

                      <SeparatorBorder className="mt-2" />

                      {hasPlanFeature(2) && <AccessType />}

                      <Statuses />

                      <FormElementWrapper>
                        <Box className="formEleContainer">
                          <LabelStyled className="labelForm">
                            {t("date")}*
                          </LabelStyled>
                          <ArchiveDateField
                            name="date"
                            placeholder="Select Date"
                            monthDropdown={true}
                            yearDropdown={true}
                            icon={DateIcon}
                          />
                        </Box>
                      </FormElementWrapper>

                      <ValidatedInputWrapper>
                        <FormGroup className="formEleContainer">
                          <LabelStyled
                            className="border-0"
                            size="15px"
                            color="var(--text)"
                          >
                            {t("number")}
                          </LabelStyled>

                          <Box className="w-100">
                            {/* Input Field */}
                            <Field
                              type="text"
                              name="number"
                              className="form-control"
                              placeholder={t("number")}
                            />
                            {/* Error Message */}
                            <ErrorMessage
                              name="number"
                              component="div"
                              className="text-danger text-sm mb-0 mt-1"
                            />
                          </Box>
                        </FormGroup>
                      </ValidatedInputWrapper>

                      <ValidatedInputWrapper>
                        <FormGroup className="formEleContainer">
                          <LabelStyled
                            className="border-0"
                            size="15px"
                            color="var(--text)"
                          >
                            {t("from")}
                          </LabelStyled>

                          <Box className="w-100">
                            {/* Input Field */}
                            <Field
                              type="text"
                              name="from"
                              className="form-control"
                              placeholder={t("from")}
                            />
                            {/* Error Message */}
                            <ErrorMessage
                              name="from"
                              component="div"
                              className="text-danger text-sm mb-0 mt-1"
                            />
                          </Box>
                        </FormGroup>
                      </ValidatedInputWrapper>

                      <ValidatedInputWrapper>
                        <FormGroup className="formEleContainer">
                          <LabelStyled
                            className="border-0"
                            size="15px"
                            color="var(--text)"
                          >
                            {t("to")}
                          </LabelStyled>

                          <Box className="w-100">
                            {/* Input Field */}
                            <Field
                              type="text"
                              name="to"
                              className="form-control"
                              placeholder={t("to")}
                            />
                            {/* Error Message */}
                            <ErrorMessage
                              name="to"
                              component="div"
                              className="text-danger text-sm mb-0 mt-1"
                            />
                          </Box>
                        </FormGroup>
                      </ValidatedInputWrapper>

                      <Box className="w-100">
                        <AccessSettings showBaseCabinetOption={true} />
                        <SeparatorBorder />

                        {/* Access Granted Through Request */}
                        {accessThroughRequest &&
                          accessThroughRequest.length > 0 && (
                            <Box>
                              <Box className="innerFoHead">
                                <SubHeadingStyled
                                  size="16px"
                                  color="var(--text)"
                                >
                                  {t("Access_granted_through_request")}
                                </SubHeadingStyled>
                              </Box>
                              <FormElementWrapper className="d-flex gap-2 flex-wrap">
                                {accessThroughRequest.map((item) => (
                                  <UserUpload key={item.id}>
                                    <Box className="userPlaceholder">
                                      <img
                                        src={item?.profile || PlaceholderImg}
                                        alt="user"
                                      />
                                    </Box>
                                    <Box className="abtUser">
                                      <SpanStyled
                                        color="var(--text)"
                                        size="14px"
                                      >
                                        {item?.from_user?.name}
                                      </SpanStyled>
                                    </Box>
                                    <MenuTriggerCross
                                      className="removeTaskTrigger"
                                      onClick={() =>
                                        handleRemoveRequest(item?.id)
                                      }
                                    />
                                  </UserUpload>
                                ))}
                              </FormElementWrapper>
                            </Box>
                          )}

                        <Box className="commonBtns w-100">
                          <ButtonStyled
                            bg="var(--bgSecondary)"
                            label={t("Cancel")}
                            color="var(--text2)"
                            type="button"
                            onClick={() =>
                              navigate(
                                `/cabinets/${id}/files/${fileId}/archives`,
                              )
                            }
                          ></ButtonStyled>
                          <ButtonStyled
                            type="submit"
                            label={t("Update")}
                            isLoading={isSubmitting}
                          ></ButtonStyled>
                        </Box>
                      </Box>
                    </FormDataContainer>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </ProfileWrapper>
  );
};

export default EditArchive;
